import React, { FC, ReactNode, useEffect, useState } from "react";

import * as devConfig from "../../../../app/configs/devConfig";
import { getAuthHeader } from "../../../../app/globalState/GlobalAuthState";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { IFirm } from "../../../../types/types";
import { A4_MM, PDF_LOGO_BOX_MM } from "./logo.utils";

// The top of an invoice PDF, drawn with the same measurements as
// buildInvoicePdf (mm on an A4 page). Units: 1cqw = 1% of the page width.

const VISIBLE_MM = 82;
const mm = (value: number) => `${(value / A4_MM.width) * 100}cqw`;
/** jsPDF puts text on its baseline; the cap height is about 0.72 em. */
const PT_TO_MM = 0.3528;
const textTop = (baselineMm: number, pt: number) =>
  mm(baselineMm - pt * PT_TO_MM * 0.72);

const useFirmDetails = () => {
  const [firm, setFirm] = useState<IFirm | null>(null);
  useEffect(() => {
    const orgId = getLinkedFirm()?.orgId;
    if (!orgId) return;
    let cancelled = false;
    fetch(`${devConfig.getServerBaseUrlForEnv()}/api/org/${orgId}`, {
      headers: {
        Accept: "application/json",
        Authorization: getAuthHeader().Authorization,
      },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => !cancelled && data && setFirm(data))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return firm;
};

const Text: FC<{
  y: number;
  pt: number;
  x?: number;
  right?: boolean;
  bold?: boolean;
  grey?: boolean;
  children: ReactNode;
}> = ({ y, pt, x, right, bold, grey, children }) => (
  <div
    className={"absolute whitespace-nowrap leading-none"}
    style={{
      top: textTop(y, pt),
      ...(right ? { right: mm(10) } : { left: mm(x ?? 10) }),
      fontSize: mm(pt * PT_TO_MM),
      fontFamily: "Helvetica, Arial, sans-serif",
      fontWeight: bold ? 700 : 400,
      color: grey ? "#5E5D5D" : "#000",
    }}>
    {children}
  </div>
);

type Props = {
  /** The logo drawn inside the 60 x 20 mm logo space (null = no logo). */
  logo: ReactNode | null;
  showLogoSpace?: boolean;
};

export const InvoiceLogoPreview: FC<Props> = ({ logo, showLogoSpace }) => {
  const firm = useFirmDetails();
  const address = firm?.locations?.[0]?.locationAddress;
  const details = [
    address?.line1,
    address
      ? `${address.city || ""}, ${address.region || ""} ${
          address.postalCode || ""
        }`
      : "",
    firm?.phoneNumbers?.[0]?.number,
    firm?.email,
  ].filter((value) => value && value.replace(/[,\s]/g, ""));
  const lines = firm ? details : ["123 Main Street", "Your City, ST 12345"];
  const firmName = firm?.displayName || "Your firm name";
  const lineY = 20 + 5 * (1 + lines.length) + 5;
  const billY = lineY + 10;

  return (
    <div
      className={
        "overflow-hidden rounded-xl border border-[#E8E8EC] bg-[#F4F5F8] p-3 sm:p-5"
      }>
      <div
        className={
          "relative w-full overflow-hidden rounded-t-md bg-white shadow-[0_2px_10px_rgba(20,22,34,0.08)]"
        }
        style={{
          aspectRatio: `${A4_MM.width} / ${VISIBLE_MM}`,
          containerType: "inline-size",
        }}
        data-testid={"invoice-preview"}>
        <div
          className={"absolute"}
          style={{
            left: mm(PDF_LOGO_BOX_MM.left),
            top: mm(PDF_LOGO_BOX_MM.top),
            width: mm(PDF_LOGO_BOX_MM.width),
            height: mm(PDF_LOGO_BOX_MM.height),
            outline: showLogoSpace ? "1px dashed #B9A9FF" : undefined,
            outlineOffset: 2,
          }}>
          {logo}
        </div>
        <Text y={10} pt={18} right bold>
          INVOICE
        </Text>
        <Text y={20} pt={13} right bold>
          {firmName}
        </Text>
        {lines.map((line, index) => (
          <Text key={index} y={25 + index * 5} pt={10} right>
            {line}
          </Text>
        ))}
        <div
          className={"absolute left-0 right-0"}
          style={{ top: mm(lineY), height: 1, background: "#BEBEBE" }}
        />
        <Text y={billY} pt={10} grey>
          BILL TO
        </Text>
        <Text y={billY + 5} pt={10} bold>
          Client name
        </Text>
        <Text y={billY + 10} pt={10} x={150} bold>
          Invoice No.:
        </Text>
        <Text y={billY + 10} pt={10} x={180}>
          1001
        </Text>
        <div
          className={
            "pointer-events-none absolute inset-x-0 bottom-0 h-[18%] bg-gradient-to-b from-transparent to-white"
          }
        />
      </div>
    </div>
  );
};
